import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { setTimeout } from 'timers';

import AuthModal from '@/components/auth/AuthModal/AuthModal';
import { ProductGrid } from '@/components/menu/ProductGrid/ProductGrid';
import { SkeletonContainer } from '@/components/skeleton/SkeletonContainer/SkeletonContainer';
import { useAuthModal } from '@/hooks/useAuthModal';
import menuBuilderData from '@/mock-data/menu-builder.json';
import type {
  MenuHierarchyRecordModelType,
  ProductModelType,
} from '@/models/api';
import type { Cart } from '@/models/cart/cart';
import { useStore } from '@/models/root-store';
import { LoadingState } from '@/utils/constants';

import { NoProductFound } from '../../menu/NoProductFound/NoProductFound';
import { ProductHeader } from '../ProductHeader/ProductHeader';

export interface IMenuBuilder {
  menuHierarchyCms?: MenuHierarchyRecordModelType;
  productHeader?: string;
  productHeaderDescription?: string;
  containerClassName?: string;
  cartToUse?: Cart;
  search?: boolean;
  eventSource?: string;
  onSwap?: (product: ProductModelType) => void;
  onPressItem?: (product: ProductModelType) => void;
  selectedProduct?: ProductModelType;
}

export const MenuBuilder: React.FC<IMenuBuilder> = observer(
  ({
    menuHierarchyCms,
    productHeader,
    productHeaderDescription,
    cartToUse: cartToUseOverride,
    search,
    eventSource,
    onSwap,
    onPressItem,
    selectedProduct,
    containerClassName = 'mt-5 min-h-screen lg:mt-6',
  }) => {
    const router = useRouter();
    const store = useStore();
    const {
      productStore: {
        filters,
        productsCount,
        isFiltering,
        productState,
        productsList,
        search: { isSearchPending, searchResultHeader },
      },
      cartStore: { draftCart },
      generalStore: { authModalOpen, setAuthModalOpen },
      cartStore,
    } = store;
    const [isAuthModalOpen, setIsAuthModalOpen] = useAuthModal();
    useEffect(() => {
      setIsAuthModalOpen(authModalOpen);
    }, [authModalOpen]);

    const cartToUse = cartToUseOverride || cartStore.cartToUse;
    const { menuBuilderPlan } = cartToUse;
    const { action, selectedPlan } = draftCart;
    const planRoute: string = selectedPlan?.planRoute || '';
    const handleSwap = onSwap || selectedPlan?.swapLineItem;
    const selectedItemPosition = selectedPlan?.selectedItemPosition;

    useEffect(() => {
      if (!cartToUse?.menuBuilderPlan) {
        cartToUse?.initOrderPlan(menuBuilderData);
      }
    }, [cartToUse?.menuBuilderPlan]);

    useEffect(() => {
      const scrollPosition =
        menuBuilderPlan?.selectedItemPosition?.scrollPosition;
      const routerPath = menuBuilderPlan?.selectedItemPosition?.routerPathname;
      if (scrollPosition && router.pathname === routerPath) {
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
          // clear the scroll position after scrolling
          menuBuilderPlan?.setScrollPosition(0);
        }, 10);
      } else {
        window.scrollTo(0, 0);
      }
    }, []);

    if (
      !productsCount &&
      filters.isFiltered &&
      !isFiltering &&
      productState === LoadingState.DONE
    ) {
      return (
        <div className="container">
          <NoProductFound
            searchText={searchResultHeader}
            filters={filters}
            menuHierarchyCms={menuHierarchyCms}
          />
        </div>
      );
    }

    if (
      productState !== LoadingState.DONE ||
      !productsCount ||
      isSearchPending
    ) {
      return (
        <div className="container">
          <SkeletonContainer />
        </div>
      );
    }

    // filter out the selected product from the list
    const filteredProductList = productsList.filter(
      (product: ProductModelType) => product.sku !== selectedProduct?.sku,
    );

    return (
      <div className={containerClassName}>
        <ProductHeader
          search={search}
          name={productHeader || menuHierarchyCms?.name || ''}
          description={
            productHeaderDescription ||
            menuHierarchyCms?.description ||
            undefined
          }
          count={filteredProductList?.length}
        />
        <ProductGrid
          action={action}
          products={filteredProductList}
          orderPlan={menuBuilderPlan}
          handleSwap={handleSwap}
          onPressItem={onPressItem}
          selectedItemPosition={selectedItemPosition}
          planRoute={planRoute}
          planIdToView={draftCart?.planIdToView}
          mode={draftCart.mode}
          eventSource={eventSource}
        />
        <AuthModal
          isOpen={isAuthModalOpen}
          onRequestClose={() => setAuthModalOpen(false)}
          navigatedFrom={router.asPath}
          forceRedirectUrl={router.asPath}
        />
      </div>
    );
  },
);
